import { Component, DestroyRef, OnInit, inject } from "@angular/core"
import { takeUntilDestroyed } from "@angular/core/rxjs-interop"
import { CommonModule, DatePipe } from "@angular/common"
import { MatButtonModule } from "@angular/material/button"
import { MatCardModule } from "@angular/material/card"
import { MatIconModule } from "@angular/material/icon"
import { RouterModule, Router, ActivatedRoute } from "@angular/router"
import { MatTabsModule } from "@angular/material/tabs"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatInputModule } from "@angular/material/input"
import { MatSelectModule } from "@angular/material/select"
import { FormsModule } from "@angular/forms"
import { Station } from "../../models/station.model"
import { StationStoreService } from "../../services/station-store.service"
import { StationSchedule, StationScheduleService } from "../../services/station-schedule.service"
import { AuthService } from "../../services/auth.service"
import { PermissionService } from "../../services/permission.service"
import { ApiService } from "../../services/api.service"
import { SnackbarService } from "../../services/snackbar.service"
import { environment } from "../../../environments/environment"
import { buildScheduleDateQueryParams } from "../../utils/schedule-date-query"
import { StationScheduleListComponent } from "../../shared/station-schedule-list/station-schedule-list.component"

@Component({
  selector: "app-manage-campaigns",
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    RouterModule,
    MatTabsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    StationScheduleListComponent,
  ],
  templateUrl: "./manage-campaigns.component.html",
  styleUrl: "./manage-campaigns.component.css",
})
export class ManageCampaignsComponent implements OnInit {
  datepipe: DatePipe = new DatePipe("en-US")
  Math = Math

  activeTabIndex = 0

  // Role control
  get isUser(): boolean {
    return (this.role || "user").toLowerCase() === "user"
  }

  // All tab
  allSchedules: StationSchedule[] = []
  allTotal = 0
  allLimit = 25
  allOffset = 0
  allFilters: any = {
    stationid: "",
    orderid: "",
    recordingid: "",
    start_date: "",
    end_date: "",
    is_auto_fill: "",
    sort_by: "scheduledtime",
    sort_order: "ASC",
  }
  loadingAll = false
  station_logo_url: string | null = null

  // Stations tab
  stations: Station[] = []
  selectedStationId: number | null = null
  stationSchedules: StationSchedule[] = []
  stationStart = ""
  stationEnd = ""
  loadingStation = false

  ordersCount = 0

  // Order context (when navigated from Orders/Dashboard)
  activeOrderId: number | null = null
  isDownloadingReceipt = false

  private readonly destroyRef = inject(DestroyRef)
  /** Bumped per All-tab request so an older page/filter response can't overwrite a newer one. */
  private allRequestSeq = 0

  constructor(
    private stationStore: StationStoreService,
    private scheduleSvc: StationScheduleService,
    private router: Router,
    private authService: AuthService,
    private permissionService: PermissionService,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private snackbar: SnackbarService,
  ) {}

  /**
   * "Am I station staff anywhere?" — derived from the assignment list, not the
   * global users.role column. That column holds the role of the first station the
   * account was ever added to and is never refreshed, so an advertiser who is
   * later given a station assignment kept being shown the advertiser-only view.
   * Intentionally station-agnostic: this page is not scoped to one station.
   */
  get isRegularUser(): boolean {
    return this.permissionService.isAdvertiserUserOnly()
  }

  get isStaffRole(): boolean {
    return !this.permissionService.isAdvertiserUserOnly()
  }

  /** Effective role for the selected station; a getter so it follows station switches. */
  get role(): string {
    return this.permissionService.getCurrentRole()
  }

  downloadReceipt(orderId: number): void {
    this.isDownloadingReceipt = true
    this.apiService
      .authenticatedGet<{ pdf_base64: string; filename?: string }>(
        `${environment.apiUrl}/orders/${orderId}/receipt`,
      )
      .subscribe({
        next: (response) => {
          const byteCharacters = atob(response.pdf_base64)
          const byteNumbers = Array.from(byteCharacters, (c) => c.charCodeAt(0))
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: "application/pdf" })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement("a")
          link.href = url
          link.download = response.filename || `RAD-Receipt-${orderId}.pdf`
          link.click()
          window.URL.revokeObjectURL(url)
          this.isDownloadingReceipt = false
        },
        error: (err) => {
          console.error("Receipt download failed", err)
          this.isDownloadingReceipt = false
          this.snackbar.error("Failed to download receipt. Please try again.")
        },
      })
  }

  ngOnInit() {
    // Clear campaign builder state when entering manage campaigns page
    localStorage.removeItem("campaignBuilderState");
    localStorage.removeItem("campaignPreview");
    localStorage.removeItem("campaignRequest");
    localStorage.removeItem("campaignPreviewDetails");
    localStorage.removeItem("wasInPreview");

    const user = this.authService.getCurrentUserInfo?.()

    // Stations come from a long-lived store stream: subscribe once, not per query-param emission.
    if (!this.isUser) {
      this.loadStations()
    }

    // listen to query params for order context
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const oidParam = params["orderid"]
      const oid = Number(oidParam)
      if (oidParam && Number.isFinite(oid)) {
        this.activeOrderId = oid
        this.allFilters = { ...this.allFilters, orderid: oid }
      } else {
        this.activeOrderId = null
        // keep other filters but clear order filter
        this.allFilters = { ...this.allFilters, orderid: "" }
      }

      // load data depending on role
      this.loadAll()
      if (!this.isUser) {
        this.loadOrdersCount()
      }
    })
  }

  onTabChange(index: number) {
    this.activeTabIndex = index
    if (index === 1 && this.selectedStationId) {
      this.loadForStation()
    }
  }

  // All tab
  loadAll(nextOffset?: number) {
    this.loadingAll = true
    if (typeof nextOffset === "number") this.allOffset = Math.max(0, nextOffset)
    const filters: any = {
      limit: this.allLimit,
      offset: this.allOffset,
      sort_by: this.allFilters.sort_by,
      sort_order: this.allFilters.sort_order,
    }
    ;["stationid", "orderid", "recordingid"].forEach((k) => {
      const v = this.allFilters[k]
      if (v !== "" && v !== null && v !== undefined) filters[k] = Number(v)
    })
    Object.assign(
      filters,
      buildScheduleDateQueryParams(this.allFilters.start_date, this.allFilters.end_date),
    )
    if (this.allFilters.is_auto_fill !== "")
      filters.is_auto_fill = this.allFilters.is_auto_fill === true || this.allFilters.is_auto_fill === "true"
    const requestSeq = ++this.allRequestSeq
    this.scheduleSvc.list(filters).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (requestSeq !== this.allRequestSeq) return
        this.allSchedules = res.schedules
        this.station_logo_url = res.station_logo_url || null
        this.allTotal = res.pagination?.total || this.allSchedules.length
        this.loadingAll = false
      },
      error: () => {
        if (requestSeq === this.allRequestSeq) this.loadingAll = false
      },
    })
  }

  resetAll() {
    this.allFilters = {
      stationid: "",
      orderid: "",
      recordingid: "",
      start_date: "",
      end_date: "",
      is_auto_fill: "",
      sort_by: "scheduledtime",
      sort_order: "ASC",
    }
    this.allOffset = 0
    this.loadAll()
  }

  // Stations tab
  loadStations() {
    this.stationStore.watchStations().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (stations) => {
        this.stations = stations
        if (this.stations.length && !this.selectedStationId) {
          this.selectedStationId = this.stations[0].id
          this.loadForStation()
        }
      },
      error: (e) => console.error("Error loading stations:", e),
    })
  }

  onStationChange() {
    this.loadForStation()
  }

  loadForStation() {
    if (!this.selectedStationId) return
    const stationId = this.selectedStationId
    this.loadingStation = true
    this.scheduleSvc
      .byStation(stationId, {
        ...buildScheduleDateQueryParams(this.stationStart, this.stationEnd),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          // Ignore a slow response for a station the user already switched away from.
          if (this.selectedStationId !== stationId) return
          this.stationSchedules = res.schedules
          this.loadingStation = false
        },
        error: () => {
          if (this.selectedStationId === stationId) this.loadingStation = false
        },
      })
  }

  loadOrdersCount() {
    // Placeholder - will be implemented when orders API is ready
    this.ordersCount = 0
  }

  // Orders tab simply reuses All tab filter by orderid
  applyOrderFilter(orderIdField: string) {
    const oid = Number(orderIdField)
    this.allFilters = { ...this.allFilters, orderid: Number.isFinite(oid) ? oid : "" }
    this.allOffset = 0
    this.loadAll()
  }

  clearOrderFilter() {
    this.activeOrderId = null
    this.allFilters = { ...this.allFilters, orderid: "" }
    this.allOffset = 0
    if (this.route.snapshot.queryParamMap.has("orderid")) {
      // Removing the param re-emits queryParams, whose handler reloads — loading here too
      // fired the same request twice.
      this.router.navigate([], { queryParams: { orderid: null }, queryParamsHandling: "merge" })
    } else {
      this.loadAll()
    }
  }

  helpWithOrder(): void {
    const orderId = this.activeOrderId
    if (!orderId) return
    const stationName = this.allSchedules[0]?.station_name || ""

    this.router.navigate(["/help"], {
      queryParams: {
        prefill: "true",
        category: "order_scheduling",
        reference_type: "order",
        reference_id: orderId,
        subject: `Issue with Order #${orderId}`,
        reference_label: `Order #${orderId} — ${stationName}`,
      },
    })
  }

  createCampaign() {
    this.router.navigate(["/campaignbuilder"])
  }

  logout() {
    this.authService.logout()
    this.router.navigate(["/login"])
  }
}
